/**
 * Presents the compiled connector catalog and opens the selected setup flow.
 *
 * Provider packages supply metadata and behavior through ConnectorPickerAction;
 * this dialog owns only the shared selection experience.
 */
import { ChevronRight } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import type { ConnectorPickerAction } from '../types'

function ConnectorOption({
  action,
  onSelect,
}: {
  action: ConnectorPickerAction
  onSelect: (action: ConnectorPickerAction) => void
}) {
  return (
    <button
      type="button"
      data-testid={`connector-picker-option-${action.id}`}
      disabled={action.disabled}
      className="group flex min-h-[76px] w-full items-center gap-4 rounded-xl border border-slate-200 px-4 py-3 text-left transition-[border-color,background-color,transform] duration-200 hover:-translate-y-px hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:border-slate-200 disabled:hover:bg-white motion-reduce:transform-none motion-reduce:transition-none"
      onClick={() => onSelect(action)}
    >
      <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-slate-100 text-slate-600">
        <action.Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-semibold leading-5 text-slate-900">
          {action.label}
        </span>
        <span className="mt-0.5 block text-[13px] leading-[18px] text-slate-500">
          {action.disabled && action.disabledReason
            ? action.disabledReason
            : action.description}
        </span>
      </span>
      {action.connectedCount > 0 && (
        <span className="shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
          {action.connectedCount} connected
        </span>
      )}
      <ChevronRight className="h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 group-enabled:group-hover:translate-x-0.5 motion-reduce:transition-none" />
    </button>
  )
}

export function ConnectorPickerDialog({
  open,
  onOpenChange,
  vaultName,
  sourceActions,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  vaultName?: string
  sourceActions: readonly ConnectorPickerAction[]
}) {
  const select = (action: ConnectorPickerAction) => {
    onOpenChange(false)
    action.onSelect()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="connector-picker-dialog" className="max-h-[calc(100vh-2rem)] gap-0 overflow-y-auto rounded-[18px] p-0 shadow-[0_32px_80px_rgba(15,23,42,0.3)] sm:max-w-[720px] [&>[data-slot=dialog-close]]:right-5 [&>[data-slot=dialog-close]]:top-5 [&>[data-slot=dialog-close]]:grid [&>[data-slot=dialog-close]]:h-8 [&>[data-slot=dialog-close]]:w-8 [&>[data-slot=dialog-close]]:place-items-center [&>[data-slot=dialog-close]]:rounded-lg [&>[data-slot=dialog-close]]:opacity-100 [&>[data-slot=dialog-close]]:text-slate-500 [&>[data-slot=dialog-close]]:hover:bg-slate-100">
        <DialogHeader className="gap-1 border-b border-slate-100 px-7 py-6 pr-16 text-left">
          <DialogTitle className="text-xl font-semibold leading-6 text-slate-950">
            Add a connector
          </DialogTitle>
          <DialogDescription className="text-sm leading-5 text-slate-500">
            New sources sync into{' '}
            <strong className="font-semibold text-slate-700">
              {vaultName ?? 'the paired workspace Vault'}
            </strong>. Choosing one opens its setup.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 px-7 pb-3 pt-6">
          {sourceActions.map(action => (
            <ConnectorOption
              key={action.id}
              action={action}
              onSelect={select}
            />
          ))}
        </div>

        <p className="px-7 pb-6 pt-1 text-[13px] leading-[18px] text-slate-400">
          Each provider can be connected once per workspace. Configure or disconnect an existing
          source from its card.
        </p>
      </DialogContent>
    </Dialog>
  )
}
